/**
 * Backend URLs for the self-service feature, supplied at runtime from the builder
 * JSON config's `apiEndpoints` block (see SelfServiceApiConfigService).
 *
 * This module does NOT import the app `environment` — URLs come from JSON only.
 * That is the dependency-inversion that lets the feature live in the shared
 * library (where the app environment isn't available). The host app is
 * responsible for putting the correct per-environment URLs into the builder JSON
 * (`assets/self-service-config/*-builder.json` → `apiEndpoints`).
 */
export interface SelfServiceApiOverrides {
  /** API base URL (required for any API call to resolve). */
  baseUrl?: string;
  /** Multi-file upload URL (falls back to baseUrl + /csv/upload-multiple). */
  uploadUrl?: string;
  /** Chat / generate URL (falls back to baseUrl + the generate path). */
  chatUrl?: string;
  /** Schema-list URL (falls back to baseUrl + `/schemas`). */
  schemaListUrl?: string;
  /** Workflow-list URL (falls back to baseUrl + `/workflow/list`). */
  workflowListUrl?: string;
}

let apiOverrides: SelfServiceApiOverrides = {};

/** Set the runtime URLs (called once when the JSON config loads). */
export function setSelfServiceApiOverrides(overrides: SelfServiceApiOverrides | undefined | null): void {
  if (!overrides) {
    apiOverrides = {};
    return;
  }

  apiOverrides = {
    ...apiOverrides,
    baseUrl: normalizeConfiguredApiUrl(overrides.baseUrl, apiOverrides.baseUrl) || apiOverrides.baseUrl,
    uploadUrl: normalizeConfiguredApiUrl(overrides.uploadUrl, apiOverrides.baseUrl) || apiOverrides.uploadUrl,
    chatUrl: normalizeConfiguredApiUrl(overrides.chatUrl, apiOverrides.baseUrl) || apiOverrides.chatUrl,
    schemaListUrl: normalizeConfiguredApiUrl(overrides.schemaListUrl, apiOverrides.baseUrl) || apiOverrides.schemaListUrl,
    workflowListUrl: normalizeConfiguredApiUrl(overrides.workflowListUrl, apiOverrides.baseUrl) || apiOverrides.workflowListUrl,
  };
}

function normalizeConfiguredApiUrl(url: string | undefined | null, baseUrl: string | undefined): string | undefined {
  const configured = String(url ?? '').trim();
  if (!configured) return undefined;
  if (/^https?:\/\//i.test(configured)) return stripTrailingSlash(configured);

  const base = stripTrailingSlash(String(baseUrl ?? '').trim());
  if (!base || !/^https?:\/\//i.test(base)) return configured;

  const path = configured.startsWith('/') ? configured : `/${configured}`;
  const baseOrigin = getUrlOrigin(base);
  const baseWithoutPlatform = base.replace(/\/spriced\/platform$/i, '');
  return path.startsWith('/spriced/platform')
    ? `${baseOrigin || baseWithoutPlatform}${path}`
    : `${base}${path}`;
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function getUrlOrigin(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

export function getSelfServiceBaseUrl(): string {
  const configured = apiOverrides.baseUrl?.trim() ?? '';
  if (!configured) {
    // JSON `apiEndpoints.baseUrl` is mandatory now (no environment fallback).
    // Warn loudly so a missing/empty config is obvious in dev; API calls will be
    // relative (and likely fail) until the host app fills it in.
    console.warn(
      '[self-service] apiEndpoints.baseUrl is not set in the builder JSON config — ' +
        'API calls will not resolve. Set assets/self-service-config/*-builder.json → apiEndpoints.baseUrl.',
    );
    return '';
  }
  return stripTrailingSlash(configured);
}

export function buildSelfServiceApiUrl(path: string): string {
  const baseUrl = getSelfServiceBaseUrl();
  // An empty path must return the bare base URL untouched — `''.startsWith('/')`
  // is false, so without this guard it fell through to `/${''}` = '/', tacking a
  // stray trailing slash onto the base (e.g. ".../spriced/platform" became
  // ".../spriced/platform/", 404ing against a backend that doesn't expect it).
  if (!path) return getSelfServicePlatformApiUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (/\/spriced\/platform$/i.test(baseUrl) && normalizedPath.startsWith('/spriced/platform')) {
    return `${baseUrl.replace(/\/spriced\/platform$/i, '')}${normalizedPath}`;
  }
  return `${baseUrl}${normalizedPath}`;
}

export function resolveSelfServiceApiUrl(configuredUrl: string | undefined | null, fallbackPath = ''): string {
  const configured = String(configuredUrl ?? '').trim();
  if (!configured) return buildSelfServiceApiUrl(fallbackPath);
  if (/^https?:\/\//i.test(configured)) return configured;
  return buildSelfServiceApiUrl(configured);
}
/**
 * A platform API URL that was SAVED into a tile, resolved against the CURRENT base URL.
 * A tile built on a dev machine carries e.g. "http://localhost:8090/spriced/platform/components/compute";
 * used as-is, the published dashboard would call that machine. A platform URL keeps only its
 * path; a relative path resolves as usual; any other absolute URL is left alone.
 */
export function rebaseSelfServiceApiUrl(savedUrl: string | undefined | null): string {
  const url = String(savedUrl ?? '').trim();
  if (!url) return '';
  if (!/^https?:\/\//i.test(url)) return buildSelfServiceApiUrl(url);
  const platformPath = url.match(/^https?:\/\/[^/]+\/spriced\/platform(\/[^?#]*)?([?#].*)?$/i);
  if (!platformPath || !apiOverrides.baseUrl?.trim()) return url;
  return `${buildSelfServiceApiUrl(platformPath[1] ?? '')}${platformPath[2] ?? ''}`;
}

export function getSelfServiceUploadApiUrl(): string {
  const override = apiOverrides.uploadUrl?.trim();
  if (override) {
    return override;
  }
  return buildSelfServiceApiUrl('/csv/upload-multiple');
}

export function getSelfServiceWorkflowDeleteFilesApiUrl(): string {
  return buildSelfServiceApiUrl('/workflow/delete/files');
}

export function getSelfServiceWorkflowUploadApiUrl(): string {
  return buildSelfServiceApiUrl('/workflow/upload');
}

export function getSelfServiceWorkflowRunApiUrl(workflowId: string): string {
  const normalizedWorkflowId = encodeURIComponent(String(workflowId || '').trim());
  return buildSelfServiceApiUrl(`/workflow/run/${normalizedWorkflowId}`);
}

export function getSelfServiceWorkbookUploadApiUrl(options?: {
  autoRun?: boolean;
  timeoutSecondsPerSheet?: number;
}): string {
  const base = buildSelfServiceApiUrl('/workflow/upload-workbook');
  const params: string[] = [];
  if (options?.autoRun) {
    params.push('autoRun=true');
  }
  if (options?.timeoutSecondsPerSheet && options.timeoutSecondsPerSheet > 0) {
    params.push(`timeoutSecondsPerSheet=${Math.floor(options.timeoutSecondsPerSheet)}`);
  }
  return params.length ? `${base}?${params.join('&')}` : base;
}

export function getSelfServiceWorkbookRunApiUrl(
  workflowNames: string[],
  timeoutSecondsPerSheet?: number,
): string {
  const names = (workflowNames || [])
    .map((name) => String(name || '').trim())
    .filter((name) => !!name);
  const params = [`workflows=${encodeURIComponent(names.join(','))}`];
  if (timeoutSecondsPerSheet && timeoutSecondsPerSheet > 0) {
    params.push(`timeoutSecondsPerSheet=${Math.floor(timeoutSecondsPerSheet)}`);
  }
  return `${buildSelfServiceApiUrl('/workflow/run-workbook')}?${params.join('&')}`;
}

export function getSelfServiceDeamApiUrl(schema = 'public'): string {
  const platformBaseUrl = getSelfServicePlatformApiUrl();
  const deamBaseUrl = platformBaseUrl.replace(/\/spriced\/platform$/i, '/spriced/deam');
  const normalizedSchema = encodeURIComponent(String(schema || 'public').trim() || 'public');
  return `${deamBaseUrl}/getDEAM?schema=${normalizedSchema}`;
}

export function getSelfServicePlatformGetDataApiUrl(): string {
  return `${getSelfServicePlatformApiUrl()}/getData`;
}

export function getSelfServicePlatformApiUrl(): string {
  const baseUrl = getSelfServiceBaseUrl();
  return /\/spriced\/platform$/i.test(baseUrl)
    ? baseUrl
    : buildSelfServiceApiUrl('/spriced/platform');
}

export function getSelfServiceEntityColumnsUpdateApiUrl(entityName: string): string {
  const normalizedEntityName = encodeURIComponent(String(entityName || '').trim());
  return buildSelfServiceApiUrl(`/api/entities/${normalizedEntityName}/columns/update`);
}

export function getSelfServiceChatApiUrl(): string {
  const override = apiOverrides.chatUrl?.trim();
  if (override) {
    return resolveSelfServiceApiUrl(override);
  }
  return `${getSelfServicePlatformApiUrl()}/ppt-dashboard/generate`;
}

/** Schema-list URL for the Data Builder's "Schema" dropdown. Override via
 *  builder JSON `apiEndpoints.schemaListUrl`; falls back to `baseUrl + /schemas`
 *  when unset (adjust the fallback path once the real backend route is
 *  confirmed). */
export function getSelfServiceSchemaListApiUrl(): string {
  const override = apiOverrides.schemaListUrl?.trim();
  if (override) {
    return resolveSelfServiceApiUrl(override);
  }
  return buildSelfServiceApiUrl('/schemas');
}

/** Workflow-list URL for the Data Builder's "Workflow" dropdown. Override via
 *  builder JSON `apiEndpoints.workflowListUrl`; falls back to
 *  `baseUrl + /workflow/list` when unset (adjust the fallback path once the
 *  real backend route is confirmed). */
export function getSelfServiceWorkflowListApiUrl(): string {
  const override = apiOverrides.workflowListUrl?.trim();
  if (override) {
    return resolveSelfServiceApiUrl(override);
  }
  return buildSelfServiceApiUrl('/workflow/list');
}

export function getSelfServiceGeneratedDashboardSaveApiUrl(
  dashboardSlug: string,
  workflow = 'fulfillment',
): string {
  const normalizedSlug = encodeURIComponent(String(dashboardSlug || '').trim());
  const normalizedWorkflow = encodeURIComponent(String(workflow || 'fulfillment').trim() || 'fulfillment');
  return `${getSelfServicePlatformApiUrl()}/ppt-dashboard/generated/${normalizedSlug}/${normalizedWorkflow}`;
}

export function unwrapArray<T>(response: unknown, keys: string[] = ['items', 'data', 'results', 'charts', 'dashboards']): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }

  if (response && typeof response === 'object') {
    const record = response as Record<string, unknown>;
    for (const key of keys) {
      if (Array.isArray(record[key])) {
        return record[key] as T[];
      }
    }
  }

  return [];
}

export function unwrapObject<T>(response: unknown, keys: string[] = ['data', 'item', 'chart', 'dashboard']): T | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const record = response as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as T;
    }
  }

  return record as T;
}

export function extractStringArray(values: unknown): string[] {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value) => String(value ?? '').trim())
    .filter((value) => value.length > 0);
}

